/**
 * Auth sagas under BFF auth.
 *
 * The chores backend owns the OAuth client. The SPA flow is:
 *
 *   - boot: GET /api/v1/auth/me → if 200, dispatch loginSuccess;
 *     if 401, the user is anonymous. Either way dispatch
 *     sessionProbeComplete so ProtectedRoute leaves the Loading state.
 *   - login: POST /api/v1/auth/start → window.location.assign to the
 *     foauth /authorize URL. The backend's GET /api/v1/auth/callback
 *     handles the code exchange and 302s back to the returnTo path
 *     with the chores_session cookie set.
 *   - logout: POST /api/v1/auth/logout → clears cookie + revokes RT
 *     family at foauth. dispatch logout() to drop user from store.
 */

import { call, put, take, fork, takeLatest } from 'redux-saga/effects';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import {
  LOGIN_REQUESTED,
  LOGOUT_REQUESTED,
  loginRequested,
  loginSuccess,
  logout,
  sessionProbeComplete,
  type AuthUser,
} from './ducks';

// ─── HTTP helpers ─────────────────────────────────────────

// Canonical OIDC userinfo shape returned by foauth/bff-go's /auth/me.
// `sub` is the foauth user id; `name` is the display name. Older
// chores in-repo BFF returned {id, display_name} — the SPA's AuthUser
// keeps that shape and toAuthUser bridges it.
interface MeUser {
  sub: string;
  email: string;
  email_verified?: boolean;
  name: string;
  role?: string;
  tenant_id?: string;
}

interface AuthStartResponse {
  authorize_url: string;
}

// BFF endpoints return plain JSON, NOT the chores {success, data, meta}
// envelope that apiGet/apiPost expect. The bff-go library writes its
// own responses; chores' writeJSON only wraps domain handlers. Use
// fetch directly so client.ts's unwrap() doesn't throw 'Unknown API
// error' on a perfectly valid response.
async function fetchMe(): Promise<MeUser | null> {
  const res = await fetch('/api/v1/auth/me', { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) return null;
  return (await res.json()) as MeUser;
}

async function startAuthFlow(returnTo: string): Promise<string> {
  const res = await fetch('/api/v1/auth/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ return_to: returnTo }),
  });
  if (!res.ok) throw new Error(`auth/start failed: ${res.status}`);
  const body = (await res.json()) as AuthStartResponse;
  return body.authorize_url;
}

async function postLogout(): Promise<void> {
  try {
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort — the cookie is HttpOnly so the SPA can't clear it
    // itself, but the server already 302s us out of the authenticated
    // surface on the next request.
  }
}

function toAuthUser(s: MeUser): AuthUser {
  return {
    id: s.sub,
    email: s.email,
    display_name: s.name,
    role: s.role,
  };
}

// ─── Login Saga ───────────────────────────────────────────

// LOGIN_REQUESTED redirects the browser to foauth via the BFF /start
// endpoint. The backend's /callback handler resumes after foauth
// redirects back, sets the cookie, and 302s to the returnTo target —
// at which point the boot saga's GET /auth/me observes the new
// session and dispatches loginSuccess.
export function* loginSaga(action: ReturnType<typeof loginRequested>): Generator {
  yield call(withSagaFlow, 'auth.login', ['auth'], action, function* () {
    yield* emitSagaPoint('bff_start');
    const returnTo = action.payload.returnTo ?? '/';
    const url = (yield call(startAuthFlow, returnTo)) as string;
    yield* emitSagaPoint('bff_redirect');
    // Hard navigation — foauth's hosted login page replaces the
    // current document.
    window.location.assign(url);
  });
}

// ─── Logout Saga ──────────────────────────────────────────

export function* logoutSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'auth.logout', ['auth'], action, function* () {
    yield call(postLogout);
    yield put(logout());
    // Send the user back to /login so the next protected route doesn't
    // briefly render before the redirect.
    if (typeof window !== 'undefined') {
      window.location.assign('/login');
    }
  });
}

// ─── Boot Saga ────────────────────────────────────────────

// Runs once on app start. Asks the backend who we are; the cookie
// (if any) does all the auth work. Always finishes by dispatching
// sessionProbeComplete so the UI leaves the boot loading state.
export function* bootAuth(): Generator {
  try {
    const me = (yield call(fetchMe)) as MeUser | null;
    if (me) {
      yield put(loginSuccess({ user: toAuthUser(me) }));
    }
  } finally {
    yield put(sessionProbeComplete());
  }
}

// ─── Watcher Saga ─────────────────────────────────────────

export function* watchAuthActions(): Generator {
  yield takeLatest(LOGIN_REQUESTED, loginSaga);
  yield takeLatest(LOGOUT_REQUESTED, logoutSaga);
}

// ─── Root Auth Saga ───────────────────────────────────────

export function* authSaga(): Generator {
  yield fork(bootAuth);
  yield fork(watchAuthActions);
}
